import React, { useEffect, useState } from "react";
import {
  Drawer,
  Form,
  Input,
  Button,
  Switch,
  Select,
  Space,
  Typography,
  Divider,
  Tag,
} from "antd";
import { DeleteOutlined, StarFilled } from "@ant-design/icons";
import { isCourseOwner } from "../data";
import { useAppContext } from "../context/AppContext";
import { listProfessors } from "../api/users";

const { Text, Title } = Typography;

const CourseEditDrawer = ({
  open,
  course,
  onClose,
  onSubmit,
  onDelete,
}) => {
  const [form] = Form.useForm();
  const { user } = useAppContext();
  const isEditing = !!course;
  const isOwner = isEditing ? isCourseOwner(course, user) : true;

  // Professors fetched from the API; co-editors are selected by user id.
  const [professors, setProfessors] = useState([]);
  useEffect(() => {
    if (!open) return;
    listProfessors()
      .then(setProfessors)
      .catch(() => setProfessors([]));
  }, [open]);

  useEffect(() => {
    if (open) {
      form.setFieldsValue({
        name: course?.name || "",
        description: course?.description || "",
        status: course?.status || "draft",
        editorIds: course?.editorIds || [],
      });
    }
  }, [open, course, form]);

  const handleFinish = (values) => {
    onSubmit({
      ...values,
      status: values.status,
    });
  };

  // The owner can't be their own co-editor.
  const excludedId = isEditing ? course.ownerId : user?.id;
  const editorOptions = professors
    .filter((p) => p.id !== excludedId)
    .map((p) => ({ label: p.full_name, value: p.id }));

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={isEditing ? "Modifier le cours" : "Créer un cours"}
      width={520}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ status: "draft", editorIds: [] }}
      >
        <Form.Item
          label="Nom du cours"
          name="name"
          rules={[{ required: true, message: "Le nom du cours est requis." }]}
        >
          <Input placeholder="Ex. Imagerie Pédiatrique" />
        </Form.Item>

        <Form.Item label="Description" name="description">
          <Input.TextArea
            rows={4}
            placeholder="Décrivez le contenu et l'objectif du cours."
          />
        </Form.Item>

        {isEditing && (
          <>
            <Divider />
            <Title level={5}>Propriétaire</Title>
            <Tag
              color="blue"
              icon={<StarFilled />}
              style={{ marginBottom: 16 }}
            >
              {course.owner}
            </Tag>
          </>
        )}

        <Form.Item
          label="Co-éditeurs"
          name="editorIds"
          tooltip="Les co-éditeurs peuvent modifier le cours et y ajouter des cas."
        >
          <Select
            mode="multiple"
            placeholder="Ajouter des co-éditeurs"
            options={editorOptions}
            optionFilterProp="label"
            disabled={isEditing && !isOwner}
            allowClear
          />
        </Form.Item>

        {isOwner && (
          <Form.Item
            label="Statut"
            name="status"
            valuePropName="checked"
            getValueFromEvent={(checked) => (checked ? "published" : "draft")}
            getValueProps={(value) => ({ checked: value === "published" })}
            tooltip="Un cours en brouillon n'est visible que par vous et vos co-éditeurs."
          >
            <Switch checkedChildren="Publié" unCheckedChildren="Brouillon" />
          </Form.Item>
        )}

        {!isOwner && isEditing && (
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            Seul le propriétaire peut modifier le statut, les co-éditeurs et
            supprimer le cours.
          </Text>
        )}

        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Space>
            <Button type="primary" htmlType="submit">
              {isEditing ? "Enregistrer" : "Créer"}
            </Button>
            <Button onClick={onClose}>Annuler</Button>
          </Space>

          {isEditing && isOwner && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                onClose();
                onDelete(course);
              }}
            >
              Supprimer
            </Button>
          )}
        </Space>
      </Form>
    </Drawer>
  );
};

export default CourseEditDrawer;
